import axios from "axios";

/** Woo export JSON (API olmadan katalog). `npm run catalog:embed` ile üretilir. */
export const PRODUCTS_FALLBACK_URL = "/products-fallback.json";

/** Yinelenen / kaldırılmış Woo kopyaları (backend `CATALOG_REMOVED_PRODUCT_IDS` ile uyumlu). */
const CATALOG_REMOVED_PRODUCT_IDS = new Set([
  "wc-19694",
  "wc-19695",
  "wc-19696",
  "wc-19697",
  "wc-19698",
]);

export function sanitizeCatalogProducts(products) {
  if (!Array.isArray(products)) return [];
  const kept = products.filter(
    (p) => p && !CATALOG_REMOVED_PRODUCT_IDS.has(String(p.id || "").trim()),
  );
  const seenNames = new Map();
  const out = [];
  for (const p of kept) {
    const key = String(p.name || "").trim().toLowerCase();
    if (!key) {
      out.push(p);
      continue;
    }
    const prev = seenNames.get(key);
    if (!prev) {
      seenNames.set(key, p);
      out.push(p);
      continue;
    }
    const wcNum = (row) => {
      const n = Number(row.wc_id);
      if (Number.isFinite(n) && n > 0) return n;
      const m = String(row.id || "").match(/^wc-(\d+)$/i);
      return m ? Number(m[1]) : Number.MAX_SAFE_INTEGER;
    };
    if (wcNum(p) < wcNum(prev)) {
      const idx = out.indexOf(prev);
      if (idx >= 0) out[idx] = p;
      seenNames.set(key, p);
    }
  }
  return out;
}

/** `index.html` katalog fetch’i bitince — React mount’tan önce/sonra snapshot gelir. */
export const CATALOG_BOOTSTRAP_EVENT = "demax-catalog-bootstrap";

let cache = null;
let inflight = null;

/**
 * Ağ beklemeden katalog var mı (önceki sayfa / index bootstrap / bellek önbelleği).
 * Ürün detayında LCP için anında hydrate etmek için kullanılır.
 */
export function getStaticCatalogSnapshotSync() {
  if (typeof window !== "undefined") {
    const boot = window.__DEMAX_CATALOG_BOOTSTRAP__;
    if (boot && Array.isArray(boot.products) && boot.products.length) {
      return sanitizeCatalogProducts(boot.products);
    }
  }
  return cache;
}

/**
 * Katalog bootstrap (veya önbellek) hazır olduğunda çağrılır; zaten veri varsa bir microtask sonra da tetiklenir.
 * Ürün detayı / grid: snapshot geç gelince yeniden hydrate için.
 */
export function subscribeCatalogBootstrap(listener) {
  if (typeof window === "undefined") return () => {};
  const wrap = () => {
    try {
      listener();
    } catch {
      /* ignore */
    }
  };
  window.addEventListener(CATALOG_BOOTSTRAP_EVENT, wrap);
  if (getStaticCatalogSnapshotSync()?.length) queueMicrotask(wrap);
  return () => window.removeEventListener(CATALOG_BOOTSTRAP_EVENT, wrap);
}

export async function fetchStaticCatalogProducts() {
  if (cache) return cache;
  if (typeof window !== "undefined") {
    const boot = window.__DEMAX_CATALOG_BOOTSTRAP__;
      if (boot && Array.isArray(boot.products) && boot.products.length) {
      if (!cache) cache = sanitizeCatalogProducts(boot.products);
      try {
        delete window.__DEMAX_CATALOG_BOOTSTRAP__;
      } catch {
        /* ignore */
      }
      if (cache) return cache;
    }
  }
  if (!inflight) {
    inflight = axios
      .get(PRODUCTS_FALLBACK_URL)
      .then((r) => {
        try {
          delete window.__DEMAX_CATALOG_BOOTSTRAP__;
        } catch {
          /* ignore */
        }
        cache = sanitizeCatalogProducts(
          Array.isArray(r.data?.products) ? r.data.products : [],
        );
        try {
          if (typeof window !== "undefined") {
            window.dispatchEvent(new CustomEvent(CATALOG_BOOTSTRAP_EVENT));
          }
        } catch {
          /* ignore */
        }
        return cache;
      })
      .finally(() => {
        inflight = null;
      });
  }
  return inflight;
}

export async function fetchStaticProductById(productId) {
  const list = await fetchStaticCatalogProducts();
  return list.find((p) => String(p.id) === String(productId)) || null;
}

/** Hot reload / yeni build sonrası (geliştirmede nadir). */
export function clearStaticCatalogCache() {
  cache = null;
}

export function isStaticCatalogOnly() {
  const v = String(process.env.REACT_APP_USE_STATIC_CATALOG || "")
    .trim()
    .toLowerCase();
  return v === "true" || v === "1" || v === "yes";
}
